import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowRight,
  Loader2,
  Calendar,
  User,
  Edit,
} from "lucide-react";
import { useDueTasks } from "@/hooks/tasks/useDueTasks";
import { Task } from "@/hooks/tasks/useTasks";
import { cn } from "@/lib/utils";
import { truncateHtmlContent } from "@/utils/htmlUtils";
import { TaskInfoPopover } from "../tasks/TaskInfoPopover";
import { EditTaskModal } from "../tasks/EditTaskModal";

const getPriorityColor = (priority: Task['priority']) => {
  switch (priority) {
    case 'urgent':
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800';
    case 'high':
      return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-800';
    case 'medium':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800';
    case 'low':
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800';
    default:
      return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700';
  }
};

const getStatusIcon = (status: Task['status']) => {
  switch (status) {
    case 'done':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case 'inProgress':
      return <Clock className="h-4 w-4 text-blue-500" />;
    case 'todo':
      return <AlertCircle className="h-4 w-4 text-orange-500" />;
    default:
      return <AlertCircle className="h-4 w-4 text-gray-500" />;
  }
};

const getDaysUntilDue = (dueDate: string) => {
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  
  const diffTime = due.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    return { text: `${Math.abs(diffDays)} days overdue`, isOverdue: true };
  } else if (diffDays === 0) {
    return { text: 'Due today', isOverdue: false };
  } else if (diffDays === 1) {
    return { text: 'Due tomorrow', isOverdue: false };
  } else {
    return { text: `Due in ${diffDays} days`, isOverdue: false };
  }
};

export const DueTasksWidget = () => {
  const { t } = useTranslation();
  const { data: dueTasks, isLoading, error, refetch } = useDueTasks();
  const [openPopovers, setOpenPopovers] = useState<Record<string, boolean>>({});
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const handleTaskUpdated = () => {
    refetch();
  };

  const togglePopover = (taskId: string, isOpen: boolean) => {
    setOpenPopovers(prev => ({ ...prev, [taskId]: isOpen }));
  };

  const handleEdit = (task: Task, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the popover
    setEditingTask(task);
  };

  const overdueTasks = dueTasks?.filter(task => {
    const due = new Date(task.due_date!);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return due < today;
  }) || [];

  const dueTodayTasks = dueTasks?.filter(task => {
    const due = new Date(task.due_date!);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return due.getTime() === today.getTime();
  }) || [];

  const totalDueTasks = (dueTasks?.length || 0);

  return (
    <>
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              {t("dashboard.dueTasks")}
            </CardTitle>
            <CardDescription>
              {totalDueTasks > 0 
                ? `${overdueTasks.length} overdue, ${dueTodayTasks.length} due today`
                : t("dashboard.noDueTasks")
              }
            </CardDescription>
          </div>
          {totalDueTasks > 0 && (
            <Badge variant="destructive" className="text-xs">
              {totalDueTasks}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {Array(3)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 animate-pulse"
                >
                  <div className="bg-gray-200 dark:bg-gray-700 rounded-sm p-2 h-8 w-8"></div>
                  <div className="space-y-2 flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
          </div>
        ) : error ? (
          <div className="text-center text-muted-foreground py-8">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-red-500" />
            <p>{t("dashboard.errorLoadingTasks")}</p>
          </div>
        ) : dueTasks && dueTasks.length > 0 ? (
          <div className="space-y-3">
            {dueTasks.slice(0, 5).map((task) => {
              const dueInfo = getDaysUntilDue(task.due_date!);
              
              return (
                <Popover
                  key={task.id}
                  open={openPopovers[task.id] || false}
                  onOpenChange={(isOpen) => togglePopover(task.id, isOpen)}
                >
                  <PopoverTrigger asChild>
                    <div
                      className="flex items-start gap-3 p-3 rounded-sm border hover:bg-muted/50 transition-colors cursor-pointer group"
                    >
                  <div className="flex-shrink-0">
                    {getStatusIcon(task.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-medium text-sm truncate">
                        {task.name}
                      </h4>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleEdit(task, e)}
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          title={t("common.edit", "Edit")}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Badge 
                          variant="outline" 
                          className={cn(
                            "text-xs border",
                            getPriorityColor(task.priority)
                          )}
                        >
                          {task.priority}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span className={cn(
                        "flex items-center gap-1",
                        dueInfo.isOverdue && "text-red-600 font-medium"
                      )}>
                        <Clock className="h-3 w-3" />
                        {dueInfo.text}
                      </span>
                      {task.assignee && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {task.assignee.first_name} {task.assignee.last_name}
                        </span>
                      )}
                    </div>
                    {task.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {truncateHtmlContent(task.description, 60)}
                      </p>
                    )}
                  </div>
                </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0 max-w-[20rem]" align="end">
                    <TaskInfoPopover task={task} onTaskUpdated={handleTaskUpdated} />
                  </PopoverContent>
                </Popover>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-8">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
            <p>{t("dashboard.noDueTasks")}</p>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button
          asChild
          variant="outline"
          size="sm"
          className="ml-auto"
        >
          <Link to="/tasks">
            {t("dashboard.viewAllTasks")}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </CardFooter>
    </Card>

    {/* Edit Task Modal */}
    {editingTask && (
      <EditTaskModal
        isOpen={!!editingTask}
        onClose={() => setEditingTask(null)}
        task={editingTask}
        onTaskUpdated={() => {
          handleTaskUpdated();
          setEditingTask(null);
        }}
      />
    )}
    </>
  );
};
